<template>
  <div id="account" class="column is-full">
    <nav class="breadcrumb" aria-label="breadcrumbs">
      <ul>
        <li v-for="(routeBreadcrumb, index) in routeBreadcrumbs" :key="index"><a :href="routeBreadcrumb.path" @click="$router.push(routeBreadcrumb.path)">{{ routeBreadcrumb.name }}</a></li>
      </ul>
    </nav>
    <section class="is-medium">
      <div class="columns">
        <div class="column is-8">
          <h4 class="title is-4">Account Information</h4>
          <b-field v-if="hasCustomer" label="Account" horizontal>
            <span class="has-text-grey-light">{{ customer.account_name }}</span>
          </b-field>
          <b-field label="Email / Username" horizontal
            :type="getFieldTypesFromValidationPath('user.email')"
            :message="getFieldMessages('user.email')">
            <b-input type="email" name="email" v-model.trim="$v.user.email.$model" :loading="$wait.is(accountInformationLoader)" :disabled="$wait.any" @input.native="set" autocomplete="email username"></b-input>
          </b-field>
          <b-field label="Name" horizontal>
            <b-field 
              :type="getFieldTypesFromValidationPath('user.first_name')"
              :message="getFieldMessages('user.first_name')">
              <b-input name="first_name" v-model.trim="$v.user.first_name.$model" :loading="$wait.is(accountInformationLoader)" :disabled="$wait.any"  @input.native="set"></b-input>
            </b-field>
            <b-field 
              :type="getFieldTypesFromValidationPath('user.last_name')"
              :message="getFieldMessages('user.last_name')">
              <b-input name="last_name" v-model.trim="$v.user.last_name.$model" :loading="$wait.is(accountInformationLoader)" :disabled="$wait.any"  @input.native="set"></b-input>
            </b-field>
          </b-field>
          <b-field label="" horizontal>
            <span v-tooltip.top="saveChangesTooltipOptions">
              <b-button type="is-primary" :loading="$wait.is(accountInformationLoader)" :disabled="! isAccountInformationSubmittable" @click="updateAccountInformation"><i class="sl-floppy-disk"></i> Save Changes</b-button>
            </span>
          </b-field>
        </div>
      </div>
    </section>    
    <section class="is-medium">
      <div class="columns">
        <div class="column is-8">
          <h4 class="title is-4">Change Password</h4>
          <b-field label="Current Password" horizontal
            :type="getFieldTypesFromValidationPath('current_password')"
            :message="getFieldMessages('current_password')">
            <b-input type="password" name="current_password" v-model.trim="$v.current_password.$model" :loading="$wait.is(changePasswordLoader)" :disabled="$wait.any" @input.native="setCurrentPassword" password-reveal></b-input>
          </b-field>
          <b-field label="New Password" horizontal
            :type="getFieldTypesFromValidationPath('password')"
            :message="getFieldMessages('password')">
            <b-input type="password" name="password" v-model.trim="$v.password.$model" :loading="$wait.is(changePasswordLoader)" :disabled="$wait.any" @input.native="setPassword" password-reveal></b-input>
          </b-field>
          <b-field label="Confirm New Password" horizontal
            :type="getFieldTypesFromValidationPath('password_confirmation')"
            :message="getFieldMessages('password_confirmation')">
            <b-input type="password" name="password_confirmation" v-model.trim="$v.password_confirmation.$model" :loading="$wait.is(changePasswordLoader)" :disabled="$wait.any" @input.native="setPasswordConfirmation" password-reveal></b-input>
          </b-field>
          <b-field label="" horizontal>
            <b-button type="is-primary" @click="changePassword" :loading="$wait.is(changePasswordLoader)" :disabled="! isChangePasswordSubmittable"><i class="sl-key-2"></i> Change Password</b-button>
          </b-field>
        </div>
      </div>
    </section>
    <section v-if="hasSubsidiaries" class="is-medium">
      <div class="columns">
        <div class="column is-8">
          <h4 class="title is-4">Subsidiaries</h4>
          <b-field label="Subsidiaries" :class="{ 'subsidiary-checkboxes': true }" horizontal>
            <template v-for="(subsidiary) in subsidiaries">
              <b-field :key="subsidiary.id" >
                <b-checkbox v-model.trim="$v.selectedSubsidiaryIds.$model" :native-value="subsidiary.id" :disabled="$wait.any">{{ subsidiary.subsidiary_name }}</b-checkbox>
              </b-field>
            </template>
          </b-field>
          <b-field label="" horizontal>
            <b-button type="is-primary" @click="updateSubsidiaries" :loading="$wait.is(subsidiariesLoader)" :disabled="! isSubsidiariesSubmittable"><i class="sl-building-3"></i> Update Subsidiaries</b-button>
          </b-field>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import cloneDeep from 'lodash/cloneDeep'
import isEqual from 'lodash/isEqual'
import pick from 'lodash/pick'
import { validations, messages } from '@/plugins/vuelidate/validations/account'

// TODO: When editing user's first and last name, it modifies the information shown in the navbar because it is mutating the vuex user object; Need to copy the user to the component's dataset after fetchData() and work off of that instead

export default {
  data: function () {
    return {
      originalUser: {},
      current_password: '',
      password: '',
      password_confirmation: '',
      selectedSubsidiaryIds: [],
      messages: messages,
      apiValidationErrors: {}
    }
  },
  computed: {
    ...mapGetters([
      'user',
      'customer',
      'subsidiaries'
    ]),
    routeBreadcrumbs () {
      return [
        ...this.$route.meta.breadcrumbs,
      ]
    },
    saveChangesTooltipOptions () {
      return {
        trigger: 'hover',
        content: `Once you've made changes to your account information, use this button to save them`
      }
    },
    hasCustomer () {
      return this.user.customer_id
    },
    hasSubsidiaries () {
      return this.subsidiaries.length
    },
    subsidiaryIds () {
      return this.user.subsidiaries.map((subsidiary) => subsidiary.id)
    },
    accountInformationLoader () {
      return 'updating account information'
    },
    changePasswordLoader () {
      return 'changing password'
    },
    subsidiariesLoader () {
      return 'updating user subsidiaries'
    },
    accountInformationPathSubset () {
      return ['user.email', 'user.first_name', 'user.last_name']
    },
    changePasswordPathSubset () {
      return ['current_password', 'password', 'password_confirmation']
    },
    subsidiariesPathSubset () {
      return ['selectedSubsidiaryIds']
    },
    accountInformationPropertySubset () {
      return this.getPropertiesFromPaths(this.accountInformationPathSubset)
    },
    changePasswordPropertySubset () {
      return this.getPropertiesFromPaths(this.changePasswordPathSubset)
    },
    isDirty () {
      return this.isAccountInformationDirty || this.isChangePasswordDirty
    },
    isAccountInformationDirty () {
      return ! isEqual(this.getAccountInformationSubset(this.user), this.getAccountInformationSubset(this.originalUser))
    },
    isChangePasswordDirty () {
      return this.$v.current_password.$dirty || this.$v.password.$dirty || this.$v.password_confirmation.$dirty 
    },
    isSubsidiariesDirty () {
      return this.$v.selectedSubsidiaryIds.$dirty
    },
    isChangePasswordInvalid () {
      return this.$v.current_password.$invalid || this.$v.password.$invalid || this.$v.password_confirmation.$invalid 
    },
    isSaving () {
      return this.$wait.is(['updating user', this.changePasswordLoader])
    },
    accountInformationHasErrors () {
      return this.$v.user.$error
    },
    changePasswordHasErrors () {
      return this.$v.current_password.$error || this.$v.password.$error || this.$v.password_confirmation.$error 
    },
    subsidiariesHasErrors () {
      return this.$v.selectedSubsidiaryIds.$error
    },
    isAccountInformationSubmittable () {
      return ! this.isSaving && this.isAccountInformationDirty && ! this.accountInformationHasErrors
    },
    isChangePasswordSubmittable () {
      return ! this.isSaving && ! this.isChangePasswordInvalid && this.isChangePasswordDirty && ! this.changePasswordHasErrors
    },
    isSubsidiariesSubmittable () {
      return ! this.isSaving && this.isSubsidiariesDirty && ! this.subsidiariesHasErrors
    }
  },
  methods: {
    ...mapActions([
      'getUser',
      'updateUser',
      // 'getUsersToSubsidiaries',
      'createUserToSubsidiary',
      'deleteUserToSubsidiary'
    ]),
    async fetchData () {
      this.$wait.start('fetching data')
      try {
        await this.getUser({
          'id': this.user.id,
          'includes': [
            'usersToSubsidiaries',
            'subsidiaries',
            'subsidiaries.addresses'
          ]
        })
        this.selectedSubsidiaryIds = this.subsidiaryIds
      } catch (error) {
        console.log(error)
      }
      this.originalUser = cloneDeep(this.user)
      this.$wait.end('fetching data')
    },
    getUserToSubsidiaryFromUserAndSubsidiary (userId, subsidiaryId) {
      return this.user.usersToSubsidiaries.find(function (userToSubsidiary) {
        return (userToSubsidiary.user_id === userId && userToSubsidiary.subsidiary_id === subsidiaryId) || null
      })
    },
    failedApiValidation (property) {
      return this.apiValidationErrors.hasOwnProperty(property)
    },
    clearApiValidationFailures (property) {
      if (this.apiValidationErrors.hasOwnProperty(property)) {
        delete this.apiValidationErrors[property]
      }
    },
    getPropertyFromPath (path) {
      return path.split('.').pop()
    },
    getPropertiesFromPaths (paths) {
      return paths.map((path) => this.getPropertyFromPath(path))
    },
    getFieldTypesFromValidationPath (validationPath) {
      return {
        'is-danger': eval(`this.$v.${ validationPath }.$error`)
      }
    },
    getFieldMessages(validationPath) {
      let validationPathArray = validationPath.split('.')
      return {...this.getFieldMessagesFromValidationPath(validationPath), ...this.getFieldMessagesFromFailedApiValidation(validationPathArray.pop())}
    },
    getFieldMessagesFromFailedApiValidation (property) {
      let fieldMessages = {}
      if (this.apiValidationErrors.hasOwnProperty(property)) {
        fieldMessages[this.apiValidationErrors[property]] = true
      }
      return fieldMessages
    },
    getFieldMessagesFromValidationPath (validationPath) {
      let validationPathArray = validationPath.split('.')
      let messages = this.messages

      while (validationPathArray.length) {
        messages = messages[validationPathArray.shift()]
      }

      let fieldMessages = {}

      for (const validation in messages) {
        fieldMessages[messages[validation]] = eval(`this.$v.${ validationPath }.$error && ! this.$v.${ validationPath }.${ validation }`)
      }

      return fieldMessages
    },
    set (e) {
      this.clearApiValidationFailures(e.target.name)
      this.$set(this.user, e.target.name, e.target.value)
    },
    getAccountInformationSubset (user) {
      return pick(user, this.accountInformationPropertySubset)
    },
    getChangePasswordSubset (user) {
      return pick(user, this.changePasswordPropertySubset)
    },
    setCurrentPassword (e) {
      this.clearApiValidationFailures(e.target.name)
      this.$set(this.user, 'current_password', e.target.value)
    },
    setPassword (e) {
      this.clearApiValidationFailures(e.target.name)
      this.$set(this.user, 'password', e.target.value)
      this.$v.password_confirmation.$touch()
    },
    setPasswordConfirmation (e) {
      this.clearApiValidationFailures(e.target.name)
      this.$set(this.user, 'password_confirmation', e.target.value)
      this.$v.password.$touch()
    },
    isValid (pathSubset) {
      let invalid = false
      for(const path of pathSubset) {
        eval(`this.$v.${ path }.$touch()`)
        invalid = invalid || eval(`this.$v.${ path }.$invalid`)
      }
      if (invalid) {
        this.$buefy.dialog.alert({
          type: 'is-danger',
          message: 'Please fix the indicated fields'
        })
        return
      }
      return true
    },
    validate (pathSubset) {
      if (! this.isValid(pathSubset)) {
        this.$buefy.dialog.alert({
          type: 'is-danger',
          message: 'Please fix the indicated fields'
        })
        return
      }
      return true
    },
    async updateAccountInformation () {
      if (this.validate(this.accountInformationPathSubset)) {
        this.$wait.start(this.accountInformationLoader)
        await this.update(this.getAccountInformationSubset(this.user))
        this.$buefy.toast.open({
          message: 'Your account information has been updated',
          type: 'is-success',
          position: 'is-top',
          duration: 5000,
        })
        this.$wait.end(this.accountInformationLoader)
      }
    },
    async changePassword () {
      if (this.validate(this.changePasswordPathSubset)) {
        this.$wait.start(this.changePasswordLoader)
        await this.update({
          // ...this.getChangePasswordSubset(this.user),
          current_password: this.current_password,
          password: this.password,
          password_confirmation: this.password_confirmation
        })
        this.current_password = ''
        this.password = ''
        this.password_confirmation = ''
        this.$buefy.toast.open({
          message: 'Your password has been updated',
          type: 'is-success',
          position: 'is-top',
          duration: 5000,
        })
        this.$wait.end(this.changePasswordLoader)
      }
    },
    async updateSubsidiaries () {
      if (this.validate(this.subsidiariesPathSubset)) {
        this.$wait.start(this.subsidiariesLoader)
        const addedSubsidiaryIds = this.selectedSubsidiaryIds.filter((subsidiaryId) => ! this.subsidiaryIds.includes(subsidiaryId))
        const removedSubsidiaryIds = this.subsidiaryIds.filter((subsidiaryId) => ! this.selectedSubsidiaryIds.includes(subsidiaryId))
        for (let i = 0; i < addedSubsidiaryIds.length; i++) { 
          await this.createUserToSubsidiary({
            data: {
              user_id: this.user.id,
              subsidiary_id: addedSubsidiaryIds[i]
            }
          })
        }
        for (let i = 0; i < removedSubsidiaryIds.length; i++) { 
          const userToSubsidiary = this.getUserToSubsidiaryFromUserAndSubsidiary(this.user.id, removedSubsidiaryIds[i])
          await this.deleteUserToSubsidiary(userToSubsidiary.id)
        }
        await this.fetchData() // Refresh user and it's subsidiaries
        this.$buefy.toast.open({
          message: 'Your subsidiaries have been updated',
          type: 'is-success',
          position: 'is-top',
          duration: 5000,
        })
        this.$wait.end(this.subsidiariesLoader)
      }
    },
    async update (data) {
      try {
        await this.updateUser({ 
          'id': this.user.id, 
          'data': data
        })
        this.originalUser = cloneDeep(this.user)
        this.$v.$reset()
      } catch (error) {
        switch (error.response.status) {
          case 422:
            this.apiValidationErrors = error.response.data.errors
            break
          default:
            console.log(error)
        }
        return Promise.reject(error)
      }
    }
  },
  validations: validations,
  created () {
    this.fetchData()
  },
  watch: {
    '$route': 'fetchData'
  },
  beforeRouteLeave (to, from, next) {
    if (this.isDirty) {
      this.$buefy.dialog.confirm({
        'type': 'is-danger',
        'message': 'Your account information has been modified but it has not been saved. Are you sure you want to leave without saving?',
        'trap-focus': true,
        'onConfirm': () => {
          next()
        }
      })
    } else {
      next()
    }
  }
}
</script>
